import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query'

import { ApiError, apiFetch } from '@/lib/api'

/*
 * A sessão da Área do Gestor: quem está logado, com que cargo e que abas o
 * cargo abre. É estado de servidor — o cookie de sessão mora no navegador, mas
 * quem diz se ele ainda vale é a API —, então vive no cache do TanStack Query
 * e não em `useState`: a guarda das rotas, o menu e o chip leem o mesmo dado.
 *
 * `null` no cache quer dizer "sem sessão" (a API respondeu 401). É um estado
 * normal da tela de entrada, e não um erro: por isso a consulta o devolve em
 * vez de lançar.
 */

/** As abas que a API pode listar para um cargo. */
export type Aba = 'visao-geral' | 'acessos' | 'cargos' | 'camadas' | 'conteudos' | 'historico'

/** O corpo de `GET /v1/auth/sessao` e de `POST /v1/auth/login`. */
export interface Sessao {
  nome: string
  email: string
  cargo: string
  abas: Aba[]
  /** Verdadeiro enquanto a pessoa não trocou a senha inicial. */
  trocarSenha: boolean
}

export interface Credenciais {
  email: string
  senha: string
}

const CHAVE = ['sessao'] as const

/** `GET /v1/auth/sessao`: os dados da sessão, ou `null` se não há sessão (401). */
export async function lerSessao(): Promise<Sessao | null> {
  try {
    return await apiFetch<Sessao>('/v1/auth/sessao')
  } catch (erro) {
    if (erro instanceof ApiError && erro.status === 401) return null
    throw erro
  }
}

/**
 * A consulta da sessão. Sem tentar de novo: 401 já é resposta (vira `null`), e
 * erro de servidor deve aparecer logo, não depois de três tentativas.
 */
export const sessaoQuery = queryOptions({
  queryKey: CHAVE,
  queryFn: lerSessao,
  retry: false,
})

/** A sessão atual (`null` sem sessão, `undefined` enquanto carrega). */
export function useSessao() {
  return useQuery(sessaoQuery)
}

/**
 * `POST /v1/auth/login`. Entrando, a API devolve os dados da sessão e eles já
 * vão para o cache, sem um segundo pedido. A recusa (401) sobe como `ApiError`
 * para a tela mostrar a mensagem. O token CSRF vai no cabeçalho
 * `X-XSRF-TOKEN`, lido do cookie que a API entrega na consulta da sessão.
 */
export async function entrar(queryClient: QueryClient, credenciais: Credenciais): Promise<Sessao> {
  const sessao = await apiFetch<Sessao>('/v1/auth/login', {
    method: 'POST',
    body: credenciais,
  })
  queryClient.setQueryData(CHAVE, sessao)
  return sessao
}

/**
 * `POST /v1/auth/logout`: invalida a sessão no servidor e limpa o cache — as
 * demais consultas da área saem junto, para os dados de uma pessoa não
 * aparecerem para a próxima que entrar no mesmo navegador.
 */
export async function sair(queryClient: QueryClient): Promise<void> {
  await apiFetch<void>('/v1/auth/logout', { method: 'POST' })
  esquecerSessao(queryClient)
}

/**
 * Apaga do cache a sessão e tudo o que foi lido com ela, e marca "sem sessão":
 * a guarda das rotas leva a pessoa à entrada. Vale para a saída e para a
 * sessão que caiu com a página aberta (expirou, foi revogada ou encerrada).
 */
export function esquecerSessao(queryClient: QueryClient): void {
  queryClient.clear()
  queryClient.setQueryData(CHAVE, null)
}

/**
 * A tela de abertura do cargo: a Visão geral (o painel) para quem registra
 * pessoas, ou seja, o administrador; Camadas para quem atualiza camadas,
 * Conteúdos para quem atualiza conteúdos e, sem nenhuma dessas, a Visão geral.
 */
export function inicioDoCargo(sessao: Sessao): string {
  if (sessao.abas.includes('acessos')) return '/gestor/painel'
  if (sessao.abas.includes('camadas')) return '/gestor/camadas'
  return sessao.abas.includes('conteudos') ? '/gestor/conteudos' : '/gestor/painel'
}

/**
 * As telas da Área do Gestor que abrem sem sessão: pedir o link de senha, pedir
 * por código, criar a senha pelo link e as três confirmações por clique. Quem
 * chega por um link de e-mail não tem sessão (e não deve ser mandado para a
 * entrada, que apagaria o link).
 */
const CAMINHOS_PUBLICOS: ReadonlySet<string> = new Set([
  '/gestor/esqueci-senha',
  '/gestor/esqueci-senha/codigo',
  '/gestor/nova-senha',
  '/gestor/confirmar-email',
  '/gestor/indicacao/confirmar',
  '/gestor/indicacao/cancelar',
])

/**
 * Para onde a guarda das rotas `/gestor/*` manda quem está em `caminho`, ou
 * `null` se pode ficar. Num lugar só porque a guarda de carregamento da rota e
 * a da tela (a sessão pode cair com a página aberta) decidem igual:
 *
 * - as telas de recuperação de senha são públicas e abrem com ou sem sessão;
 * - sem sessão, só a entrada (`/gestor`) abre;
 * - com a senha inicial por trocar, só a troca de senha abre;
 * - com sessão em ordem, a entrada e a troca de senha não servem mais: vai
 *   para a abertura do cargo.
 */
export function destinoDaGuarda(sessao: Sessao | null | undefined, caminho: string): string | null {
  const atual = caminho.length > 1 ? caminho.replace(/\/+$/, '') : caminho
  if (CAMINHOS_PUBLICOS.has(atual)) return null
  if (!sessao) return atual === '/gestor' ? null : '/gestor'
  if (sessao.trocarSenha) return atual === '/gestor/senha' ? null : '/gestor/senha'
  return atual === '/gestor' || atual === '/gestor/senha' ? inicioDoCargo(sessao) : null
}
